"use client"

import { useState, useMemo } from "react"
import { useRouter, usePathname, useSearchParams } from "next/navigation"
import { useT } from "@lib/i18n/use-t"
import type { StoreCategorySeller } from "@lib/data/sellers"

const DEFAULT_VISIBLE = 5

/**
 * Multi-select seller filter rendered above the manufacturer/category
 * lists on a category page. URL contract: `?sellers=slug1,slug2`.
 *
 * Backend supports a comma-separated seller_slug query on
 * `/store/products`. If only single-select ships, we still send the
 * comma list — backend can pick the first or expand it later.
 */
export default function SellerFilter({
  sellers,
}: {
  sellers: StoreCategorySeller[]
}) {
  const t = useT()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [showAll, setShowAll] = useState(false)

  const selected = useMemo(() => {
    const raw = searchParams.get("sellers") || ""
    return new Set(
      raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    )
  }, [searchParams])

  if (!sellers.length) return null

  const sorted = [...sellers].sort(
    (a, b) => b.product_count - a.product_count
  )
  const visible = showAll ? sorted : sorted.slice(0, DEFAULT_VISIBLE)
  const overflow = Math.max(0, sorted.length - DEFAULT_VISIBLE)

  const toggle = (slug: string) => {
    const next = new Set(selected)
    if (next.has(slug)) {
      next.delete(slug)
    } else {
      next.add(slug)
    }
    const params = new URLSearchParams(searchParams.toString())
    params.delete("page")
    if (next.size) {
      params.set("sellers", Array.from(next).join(","))
    } else {
      params.delete("sellers")
    }
    const qs = params.toString()
    router.push(qs ? `${pathname}?${qs}` : pathname)
  }

  return (
    <div className="rounded-2xl border border-slate-200/60 bg-white p-3 dark:border-white/8 dark:bg-[#111827]">
      <div className="mb-2 flex items-center justify-between px-1.5">
        <span className="text-[10px] font-bold uppercase tracking-widest text-brand-light dark:text-brand-dark">
          {t("seller")} ({sorted.length})
        </span>
      </div>
      <ul className="space-y-0.5">
        {visible.map((s) => {
          const checked = selected.has(s.slug)
          return (
            <li key={s.seller_id}>
              <label className="flex cursor-pointer items-center justify-between rounded-lg px-2 py-1.5 text-xs transition hover:bg-slate-50 dark:hover:bg-white/5">
                <span className="flex min-w-0 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(s.slug)}
                    className="h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-brand-light focus:ring-brand-light/40 dark:border-white/20 dark:bg-transparent"
                  />
                  <span
                    className={`truncate ${
                      checked
                        ? "font-semibold text-brand-light dark:text-brand-dark"
                        : "text-slate-700 dark:text-slate-300"
                    }`}
                  >
                    {s.display_name}
                  </span>
                </span>
                <span className="ml-2 shrink-0 text-[10px] tabular-nums text-slate-400">
                  {s.product_count}
                </span>
              </label>
            </li>
          )
        })}
      </ul>
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-1 w-full rounded-lg px-2 py-1 text-left text-[11px] font-medium text-brand-light hover:bg-slate-50 dark:text-brand-dark dark:hover:bg-white/5"
        >
          {showAll
            ? `− ${t("close")}`
            : `${t("sellerShowMore")} (${overflow})`}
        </button>
      )}
    </div>
  )
}
